import { useState, useEffect } from 'react';
import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { ConversionStatus, SourceSystem } from '@types';

export interface FilterValues {
  status?: ConversionStatus;
  source_system?: SourceSystem;
  start_date?: string;
  end_date?: string;
  search?: string;
}

interface HistoryFiltersProps {
  filters: FilterValues;
  onFiltersChange: (filters: FilterValues) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
}

const STATUS_OPTIONS: Array<{ value: ConversionStatus | ''; label: string }> = [
  { value: '', label: 'All Statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const SOURCE_SYSTEM_OPTIONS: Array<{
  value: SourceSystem | '';
  label: string;
}> = [
  { value: '', label: 'All Systems' },
  { value: 'QUICKBOOKS', label: 'QuickBooks' },
  { value: 'ADP', label: 'ADP' },
  { value: 'PAYCHEX', label: 'Paychex' },
  { value: 'GUSTO', label: 'Gusto' },
  { value: 'WORKDAY', label: 'Workday' },
];

export function HistoryFilters({
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
}: HistoryFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [localFilters, setLocalFilters] = useState<FilterValues>(filters);

  // Update local filters when parent filters change
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Count active filters
  const activeFilterCount = Object.values(filters).filter(
    value => value && value !== ''
  ).length;

  const handleFilterChange = (key: keyof FilterValues, value: string) => {
    const newFilters = { ...localFilters, [key]: value || undefined };
    setLocalFilters(newFilters);
  };

  const handleApply = () => {
    onFiltersChange(localFilters);
    onApplyFilters();
  };

  const handleClear = () => {
    setLocalFilters({});
    onClearFilters();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFilterChange('search', e.target.value);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6">
      {/* Filter header */}
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-medium text-gray-900">Filters</h3>
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                {activeFilterCount} active
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm font-medium text-blue-600 hover:text-blue-500 lg:hidden"
          >
            {isExpanded ? 'Hide' : 'Show'}
          </button>
        </div>
      </div>

      {/* Filter controls */}
      <div
        className={`px-4 py-4 transition-all duration-200 ${
          isExpanded ? 'block' : 'hidden lg:block'
        }`}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {/* Search input */}
          <div className="xl:col-span-2">
            <label
              htmlFor="search"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Search filename
            </label>
            <input
              type="text"
              id="search"
              name="search"
              value={localFilters.search || ''}
              onChange={handleSearchChange}
              placeholder="Search by filename..."
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          {/* Source system dropdown */}
          <div>
            <label
              htmlFor="source_system"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Source System
            </label>
            <select
              id="source_system"
              name="source_system"
              value={localFilters.source_system || ''}
              onChange={e =>
                handleFilterChange('source_system', e.target.value)
              }
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              {SOURCE_SYSTEM_OPTIONS.map(option => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Status dropdown */}
          <div>
            <label
              htmlFor="status"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Status
            </label>
            <select
              id="status"
              name="status"
              value={localFilters.status || ''}
              onChange={e => handleFilterChange('status', e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              {STATUS_OPTIONS.map(option => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Date range - From */}
          <div className="md:col-span-2 lg:col-span-1">
            <label
              htmlFor="start_date"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              From Date
            </label>
            <input
              type="date"
              id="start_date"
              name="start_date"
              value={localFilters.start_date || ''}
              onChange={e => handleFilterChange('start_date', e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          {/* Date range - To */}
          <div className="md:col-span-2 lg:col-span-1">
            <label
              htmlFor="end_date"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              To Date
            </label>
            <input
              type="date"
              id="end_date"
              name="end_date"
              value={localFilters.end_date || ''}
              onChange={e => handleFilterChange('end_date', e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={handleApply}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
          >
            <FunnelIcon className="h-4 w-4" />
            Apply Filters
          </button>
          <button
            type="button"
            onClick={handleClear}
            disabled={activeFilterCount === 0}
            className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
          >
            <XMarkIcon className="h-4 w-4" />
            Clear Filters
          </button>
        </div>
      </div>
    </div>
  );
}
